import { UPLOAD_LIMITS } from '@lite-workspace/uploader-core';
import { RAW_EXTENSIONS } from '@lite-workspace/shared/shared-public';

export type UploadItemStatus = 'uploading' | 'registering' | 'processing' | 'ready' | 'failed';

export type UploadFailureReason =
  | 'too_large'
  | 'unsupported_type'
  | 'too_large_dimensions'
  | 'scene_mismatch'
  | 'duplicate'
  | 'raw_disabled'
  | 'processing'
  | 'network'
  | 'unknown';

// Одна подсказка на любой отказ обработки: сервер причину не отдаёт, а фотографу от неё всё
// равно нужно одно и то же (спека «Загрузка фото» п. 33).
export const PROCESSING_FAILED_HINT = 'Ошибка обработки. Загрузите файл ещё раз';

/** Жёлтый бейдж — повтор имеет смысл (сеть, неизвестный сбой); красный — окончательно (спека п. 31). */
export type UploadFailureSeverity = 'retryable' | 'final';

export function uploadFailureSeverity(reason: UploadFailureReason | undefined): UploadFailureSeverity {
  return reason === undefined || reason === 'network' || reason === 'unknown' ? 'retryable' : 'final';
}

export const UPLOAD_FAILURE_MESSAGES: Record<UploadFailureReason, string> = {
  too_large: 'Файл слишком большой',
  unsupported_type: 'Неподдерживаемый тип файла',
  too_large_dimensions: 'Видео больше 4K (3840×2160)',
  scene_mismatch: 'Не удалось добавить в выбранную сцену',
  duplicate: 'Такой файл уже загружен в этот проект',
  raw_disabled: 'RAW-файлы выключены — включите их в настройках аккаунта',
  processing: PROCESSING_FAILED_HINT,
  network: 'Нет соединения — проверьте интернет',
  unknown: 'Не удалось загрузить файл',
};

// Per-type upload limits: JPEG/PNG = the shared photo cap (UPLOAD_LIMITS, what the backend
// accepts), GIF up to 85 MB, video (MP4/MOV/WEBM) capped at 4K resolution.
// RAW (plan-gated) is not accepted here yet.
const MB = 1024 * 1024;

// Video ceilings: a byte guard well below the uploader's own limit, and the 4K
// pixel dimensions from the spec (checked async via a <video> element).
const VIDEO_MAX_BYTES = 500 * MB;
export const VIDEO_MAX_WIDTH = 3840;
export const VIDEO_MAX_HEIGHT = 2160;

interface FormatRule {
  readonly mimes: readonly string[];
  readonly extensions: readonly string[];
  readonly maxBytes: number;
  readonly isVideo?: boolean;
}

// RAW originals: account-gated; the platform cuts their previews, the tile badge covers the wait.
export { RAW_EXTENSIONS };
const RAW_MAX_BYTES = 200 * MB;

export function isRawFile(file: File): boolean {
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return (RAW_EXTENSIONS as readonly string[]).includes(ext);
}

// Дата файла на диске — запасной ориентир сортировки «по дате съёмки», когда в кадре
// нет EXIF. Битую или пустую дату не отправляем: без неё сервер берёт время загрузки.
export function fileModifiedAtPayload(file: File): { fileModifiedAt: string } | undefined {
  const ms = file.lastModified;
  return Number.isFinite(ms) && ms > 0 ? { fileModifiedAt: new Date(ms).toISOString() } : undefined;
}

const FORMAT_RULES: readonly FormatRule[] = [
  { mimes: ['image/jpeg', 'image/jpg'], extensions: ['jpg', 'jpeg'], maxBytes: UPLOAD_LIMITS.photoMaxBytes },
  { mimes: ['image/png'], extensions: ['png'], maxBytes: UPLOAD_LIMITS.photoMaxBytes },
  // The server decodes HEIC and cuts JPEG versions; the tile has no local frame until then.
  { mimes: ['image/heic', 'image/heif'], extensions: ['heic', 'heif'], maxBytes: UPLOAD_LIMITS.photoMaxBytes },
  { mimes: ['image/gif'], extensions: ['gif'], maxBytes: 85 * MB },
  {
    mimes: ['video/mp4', 'video/quicktime', 'video/webm'],
    extensions: ['mp4', 'mov', 'webm'],
    maxBytes: VIDEO_MAX_BYTES,
    isVideo: true,
  },
];

const RAW_RULE: FormatRule = { mimes: [], extensions: [...RAW_EXTENSIONS], maxBytes: RAW_MAX_BYTES };

function ruleForFile(file: File): FormatRule | undefined {
  // Extension wins for RAW: vendor MIMEs (image/x-canon-cr2 …) are unreliable
  // and often empty; the account flag is enforced AFTER ensureProject, where
  // the store learns rawUploadEnabled (raw_disabled beats a generic reject).
  if (isRawFile(file)) return RAW_RULE;
  const type = file.type.toLowerCase();
  if (type) {
    const byMime = FORMAT_RULES.find(rule => rule.mimes.includes(type));
    if (byMime) return byMime;
    // A non-empty type that no rule matches is a definitive unsupported type.
    if (type.startsWith('image/') || type.startsWith('video/')) return undefined;
    return undefined;
  }

  // Browsers sometimes report an empty type; fall back to the extension.
  const ext = file.name.split('.').pop()?.toLowerCase() ?? '';
  return FORMAT_RULES.find(rule => rule.extensions.includes(ext));
}

export function isVideoFile(file: File): boolean {
  return ruleForFile(file)?.isVideo === true;
}

// Client-side pre-check so a wrong-type or over-limit file fails INSTANTLY with the
// exact reason (spec §5: "File type not supported" / "Size exceeds specified limit"),
// instead of round-tripping to the uploader and getting a generic server error.
export function validateUploadFile(file: File): UploadFailureReason | null {
  const rule = ruleForFile(file);
  if (!rule) return 'unsupported_type';
  if (file.size > rule.maxBytes) return 'too_large';
  return null;
}

// The 4K resolution ceiling is a video property only readable after the browser
// decodes metadata, so it's a SEPARATE async check the caller runs after the sync
// pre-check passes. Resolves 'too_large_dimensions' when over 4K, null when OK, and
// null (not a failure) when the browser can't read the metadata — the server stays
// the backstop rather than blocking a possibly-fine upload on a decode quirk.
export function validateVideoDimensions(file: File): Promise<UploadFailureReason | null> {
  if (!isVideoFile(file)) return Promise.resolve(null);
  return new Promise(resolve => {
    const url = URL.createObjectURL(file);
    const video = document.createElement('video');
    video.preload = 'metadata';
    const done = (reason: UploadFailureReason | null) => {
      URL.revokeObjectURL(url);
      resolve(reason);
    };

    video.onloadedmetadata = () => {
      const overSized = video.videoWidth > VIDEO_MAX_WIDTH || video.videoHeight > VIDEO_MAX_HEIGHT;
      done(overSized ? 'too_large_dimensions' : null);
    };

    video.onerror = () => done(null);
    video.src = url;
  });
}

export interface UploadItem {
  fileId: string;
  name: string;
  status: UploadItemStatus;
  failure?: UploadFailureReason;
  /** blob: адрес выбранного файла — кадр под спиннером, пока сервер не отдал превью. */
  previewUrl?: string;
  /** Сцена, в которую файл заведён — только её сетка показывает его плитку в полёте. */
  sceneId?: string;
  /** Фото, которое сервер завёл под этот файл; есть — плитка сетки берёт слот на себя. */
  photoId?: string;
}

/** Плитка в полёте: файл текущей пачки, у которого ещё нет фото в сцене (s6-ac5). */
export interface PendingUploadTile {
  fileId: string;
  name: string;
  sceneId: string | null;
  photoId: string | null;
  previewUrl: string | null;
  failed: boolean;
  /** Цвет бейджа отказа (спека п. 31); null — файл ещё в полёте. */
  severity: UploadFailureSeverity | null;
  /** Подсказка под бейджем — причина отказа. */
  hint: string | null;
}

// fileUrl is the uploaded file's public static address, built from the uploader
// response — the photo path ignores it; the native-video path persists it verbatim
// as the video row's source (played by a guest <video src>).
// fileId is the id the platform STORED the file under. With reuseDuplicate the uploader
// answers a re-sent file with the FileDto of the copy the project already holds, so it can
// differ from the id the browser minted; register must name the stored one. Absent = the
// browser's own id was stored (mocks and callers without a FileDto).
export type UploadResult = { ok: true; fileUrl: string | null; fileId?: string } | { ok: false; reason: UploadFailureReason };

// Server contract of register: `registered` (new photo), `already_registered` (this file is
// already a photo of THIS gallery) and `refused` for any other file, for example: not a file of
// the gallery's own project, held by another gallery or under the same id by another place (a
// cover, the library, a brand asset, a portfolio, a scene video), its path queued for deletion,
// or a RAW file while RAW uploads are off. The wire words are mapped to what the queue shows the photographer.
export type RegisterOutcomeStatus = 'created' | 'duplicate' | 'scene_mismatch' | 'refused';

export interface RegisterOutcome {
  fileId: string;
  status: RegisterOutcomeStatus;
  photoId: string | null;
}

export type SyncPhotoStatus = 'ready' | 'pending' | 'failed';

export interface SyncResult {
  photos: Record<string, { status: SyncPhotoStatus }>;
  // Server signal: the bounded EXIF-backfill batch came back full — more pre-migration
  // rows remain, call sync again (drives the manage-page drain loop).
  exifBacklog?: boolean;
}
